/**
 * The engine's error line with Retry / Dismiss (chat/voice parity #1731,
 * Task 2.3). Ported verbatim from components/chat/MessageList.tsx's error
 * block: the core list renders an error as a plain alert, and TRF's page
 * needs the two actions (B-597 retry, dismiss) it always had.
 */
import { useTranslation } from 'react-i18next';
import { isEnglishUi, useUiLanguage } from './language';
import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function ChatErrorRow({ error, onRetry, onDismiss }: { error: string; onRetry?: () => void; onDismiss?: () => void }) {
  const { t } = useTranslation('chat');
  // #2109 R3-B11: the engine's and the server's error sentences are English;
  // only an English reader sees one. Everyone else reads the localized line.
  const shown = isEnglishUi(useUiLanguage()) ? error : t('errorRow.generic');
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-destructive/10 text-destructive text-body" role="alert">
      <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span className="flex-1">{shown}</span>
      {onRetry && (
        <Button variant="ghost" size="sm" onClick={onRetry} className="h-7 gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" />
          {t('errorRow.retry')}
        </Button>
      )}
      {onDismiss && (
        <Button variant="ghost" size="icon" onClick={onDismiss} className="h-6 w-6 flex-shrink-0 text-destructive hover:text-destructive" aria-label={t('errorRow.dismiss')}>
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
